<template>
    <div class="wrapper flex flex-column w-full h-full min-h-screen">
        <CustomBreadcrumb class="flex align-self-start w-full p-4 mb-2"
            :items="[{ label: 'Profile Settings' }, { label: 'Payment Method' }]" />

        <div class="pm-page px-4">

            <!-- Loading -->
            <div v-if="cardLoading" class="pm-loading">
                <ProgressSpinner style="width: 40px; height: 40px" />
                <span class="text-600">Loading card details...</span>
            </div>

            <!-- No card saved -->
            <div v-else-if="!cardInfo?.hasToken" class="pm-empty">
                <div class="pm-empty-icon-wrap">
                    <i class="pi pi-credit-card pm-empty-icon"></i>
                </div>
                <p class="pm-empty-title">No payment method saved</p>
                <p class="pm-empty-desc">
                    Add a card to enable automatic billing and seamless plan upgrades.
                </p>
                <Button
                    label="Add Payment Card"
                    icon="pi pi-plus"
                    class="pm-add-btn orange-button"
                    @click="handleCardAction('add_card')"
                />
                <p class="pm-security">
                    <i class="pi pi-lock" style="font-size: 11px"></i>
                    <a class="pm-security-link" href="https://www.payhere.lk/" target="_blank" rel="noopener noreferrer">
                        Secured by PayHere
                    </a>
                    &nbsp;·&nbsp; Visa &amp; Mastercard accepted
                </p>
            </div>

            <!-- Card saved -->
            <div v-else class="pm-card-section">
                <p class="pm-section-label">Your saved payment method</p>

                <div class="pm-card-box">
                    <div class="pm-card-icon-wrap">
                        <i class="pi pi-credit-card pm-card-icon"></i>
                    </div>
                    <div class="pm-card-details">
                        <p class="pm-card-number">{{ cardInfo.cardNo }}</p>
                        <p class="pm-card-holder">{{ cardInfo.cardHolderName }}</p>
                        <p class="pm-card-expiry">Expires {{ cardInfo.cardExpiry }}</p>
                    </div>
                    <div class="pm-card-badge">
                        <i class="pi pi-shield" style="font-size: 11px"></i>
                        <span>Active</span>
                    </div>
                </div>

                <Button
                    label="Change Card"
                    icon="pi pi-sync"
                    class="pm-change-btn orange-button"
                    @click="handleCardAction('replace_card')"
                />

                <p class="pm-security">
                    <i class="pi pi-lock" style="font-size: 11px"></i>
                    <a class="pm-security-link" href="https://www.payhere.lk/" target="_blank" rel="noopener noreferrer">
                        Secured by PayHere
                    </a>
                    &nbsp;·&nbsp; Visa &amp; Mastercard accepted
                </p>
            </div>

            <!-- Payment disclaimer -->
            <div v-if="!cardLoading" class="pm-disclaimer">
                <h3 class="text-lg font-semibold mb-2" style="color: #dc2626;">Disclaimer</h3>
                <p class="text-700 text-sm m-0">
                    Payments are securely processed through Payhere. Applicable payment processing fees
                    charged by the payment gateway are detected separately from the transaction amount.
                </p>
            </div>

        </div>

        <!-- Billing Details Dialog -->
        <BillingDetailsDialog
            v-model="showCardBillingDialog"
            :context="cardBillingContext"
            :existing-card="cardInfo"
            :initial-values="billingInitialValues"
            @submit="submitCardBilling"
        />

        <!-- Progress Dialog -->
        <Dialog
            v-model:visible="showProgressDialog"
            modal
            :closable="false"
            header="Card Verification"
            :style="{ width: 'min(420px, 92vw)' }"
        >
            <div class="flex flex-column align-items-center py-4 gap-4">
                <ProgressSpinner style="width: 50px; height: 50px" />
                <p class="text-600 text-center m-0">{{ progressMessage }}</p>
            </div>
        </Dialog>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import CustomBreadcrumb from '@/components/CustomBreadcrumb.vue';
import BillingDetailsDialog from '@/components/payments/BillingDetailsForm.vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import ProgressSpinner from 'primevue/progressspinner';
import { useAuthStore } from '@/stores/authStore';
import payments from '@/api/payments';
import { PAYMENT_ENDPOINTS } from '@/api/endpoints';
import { notifyError } from '@/utils/notification';

const CARD_PENDING_KEY = 'studentCardTokenizePending';

const authStore = useAuthStore();

const cardInfo            = ref(null);
const cardLoading         = ref(false);
const showCardBillingDialog = ref(false);
const cardBillingContext  = ref('add_card');
const showProgressDialog  = ref(false);
const progressMessage     = ref('');

const billingInitialValues = computed(() => {
    const u = authStore.user;
    if (!u) return {};
    return {
        firstName: u.firstName    || '',
        lastName:  u.lastName     || '',
        email:     u.email        || '',
        phone:     u.mobileNumber || '',
        address:   u.address      || '',
        city:      u.city         || '',
    };
});

async function fetchCardInfo() {
    cardLoading.value = true;
    try {
        const res = await payments.getMyToken();
        cardInfo.value = res?.data?.data ?? { hasToken: false };
    } catch {
        cardInfo.value = { hasToken: false };
    } finally {
        cardLoading.value = false;
    }
}

function handleCardAction(context) {
    cardBillingContext.value = context;
    showCardBillingDialog.value = true;
}

function redirectToPayHere(paymentData, preapprove = false) {
    const isSandbox = paymentData.sandbox === 'true' || paymentData.sandbox === true;
    const url = preapprove
        ? (isSandbox ? PAYMENT_ENDPOINTS.PAYHERE_SANDBOX_PREAPPROVE_URL : PAYMENT_ENDPOINTS.PAYHERE_LIVE_PREAPPROVE_URL)
        : (isSandbox ? PAYMENT_ENDPOINTS.PAYHERE_SANDBOX_URL : PAYMENT_ENDPOINTS.PAYHERE_LIVE_URL);
    payments.processPayHereRedirect({ ...paymentData, action: url }, {});
}

async function submitCardBilling(formData) {
    showCardBillingDialog.value = false;
    progressMessage.value    = 'Starting card verification...';
    showProgressDialog.value = true;
    try {
        const payload = {
            firstName: formData.firstName,
            lastName:  formData.lastName,
            email:     formData.email,
            phone:     formData.phone,
            address:   formData.address,
            city:      formData.city,
            country:   'Sri Lanka',
        };
        const res = await payments.tokenize(payload);
        const paymentData = res?.data?.data;
        showProgressDialog.value = false;
        sessionStorage.setItem(CARD_PENDING_KEY, JSON.stringify({ ts: Date.now() }));
        redirectToPayHere(paymentData, true);
    } catch (err) {
        showProgressDialog.value = false;
        const msg = err?.response?.data?.message || '';
        notifyError('Error', msg || 'Failed to start card verification. Please try again.');
    }
}

onMounted(() => {
    fetchCardInfo();
});
</script>

<style scoped>
.pm-page {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 2rem;
    padding-bottom: 3rem;
}

/* ── Loading ──────────────────────────────────── */
.pm-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    padding: 4rem 0;
}

/* ── No card state ────────────────────────────── */
.pm-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: 380px;
    padding: 2rem 1rem;
}

.pm-empty-icon-wrap {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: #FEE2E2;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.25rem;
}

.pm-empty-icon {
    font-size: 32px;
    color: #ec0e37;
}

.pm-empty-title {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 0.5rem;
}

.pm-empty-desc {
    font-size: 13px;
    color: #6B7280;
    line-height: 1.6;
    margin: 0 0 1.75rem;
}

.pm-add-btn {
    color: #fff !important;
    font-size: 14px !important;
    font-weight: 600 !important;
    padding: 10px 32px !important;
    border-radius: 10px !important;
    min-width: 210px;
    margin-bottom: 1.25rem;
}

/* ── Card saved state ─────────────────────────── */
.pm-card-section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    max-width: 420px;
}

.pm-section-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    color: #9CA3AF;
    margin: 0 0 0.875rem;
}

/* Card row box */
.pm-card-box {
    width: 100%;
    border: 1.5px solid #E5E7EB;
    border-radius: 16px;
    padding: 1.125rem 1.25rem;
    background: #fff;
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.pm-card-icon-wrap {
    width: 50px;
    height: 50px;
    border-radius: 12px;
    background: #EFF6FF;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.pm-card-icon {
    font-size: 22px;
    color: #2563EB;
}

.pm-card-details {
    flex: 1;
    min-width: 0;
}

.pm-card-number {
    font-size: 14px;
    font-family: 'Courier New', monospace;
    letter-spacing: 0.1em;
    font-weight: 700;
    color: #111827;
    margin: 0 0 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.pm-card-holder {
    font-size: 13px;
    color: #374151;
    font-weight: 500;
    margin: 0 0 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.pm-card-expiry {
    font-size: 12px;
    color: #9CA3AF;
    margin: 0;
}

.pm-card-badge {
    display: flex;
    align-items: center;
    gap: 4px;
    background: #DCFCE7;
    border: 1px solid #BBF7D0;
    border-radius: 20px;
    padding: 4px 10px;
    font-size: 11px;
    font-weight: 600;
    color: #16A34A;
    flex-shrink: 0;
    white-space: nowrap;
}

/* ── Change card button ───────────────────────── */
.pm-change-btn {
    color: #fff !important;
    font-size: 14px !important;
    font-weight: 600 !important;
    padding: 10px 28px !important;
    border-radius: 10px !important;
    width: 100%;
    justify-content: center;
    margin-bottom: 1rem;
}

/* ── Security note ────────────────────────────── */
.pm-security {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    color: #9CA3AF;
    margin: 0;
    align-self: center;
}

.pm-security-link {
    font: inherit;
    font-size: inherit;
    color: #6B7280;
    text-decoration: none;
}

.pm-security-link:hover {
    color: #1D4ED8;
    text-decoration: underline;
}

.pm-disclaimer {
    width: 100%;
    max-width: 640px;
    text-align: center;
    padding-top: 1.5rem;
    margin-top: 1rem;
}

/* ── Mobile ───────────────────────────────────── */
@media (max-width: 480px) {
    .pm-page {
        align-items: stretch;
    }

    .pm-card-section,
    .pm-empty,
    .pm-disclaimer {
        max-width: 100%;
    }

    .pm-add-btn {
        width: 100%;
        justify-content: center;
    }

    .pm-card-box {
        padding: 1rem;
    }

    .pm-card-badge {
        display: none;
    }
}
</style>
